import { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Button,
  Stepper,
  Step,
  StepLabel,
  alpha,
  useTheme
} from '@mui/material'
import {
  CheckCircle,
  Error as ErrorIcon,
  CloudDone
} from '@mui/icons-material'
import { apiClient } from '@shared/services/ApiClient'

interface MarketplaceLandingResponse {
  success: boolean
  message: string
  tenantId?: string
  subscriptionId?: string
  planId?: string
  redirectUrl?: string
}

const ACTIVATION_STEPS = [
  'Verifying purchase',
  'Creating your workspace',
  'Activating subscription',
  'Setting up your account'
]

export default function MarketplaceLanding() {
  const theme = useTheme()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [response, setResponse] = useState<MarketplaceLandingResponse | null>(null)
  const [activeStep, setActiveStep] = useState(0)

  const token = searchParams.get('token')

  useEffect(() => {
    if (!token) {
      setError('Missing marketplace token. Please check your email and try the link again.')
      setLoading(false)
      return
    }

    activateMarketplaceSubscription()
  }, [token])

  const activateMarketplaceSubscription = async () => {
    if (!token) return

    try {
      setLoading(true)
      setError(null)

      // Simulate progress through steps
      const stepInterval = setInterval(() => {
        setActiveStep((prev) => {
          if (prev < ACTIVATION_STEPS.length - 1) {
            return prev + 1
          }
          clearInterval(stepInterval)
          return prev
        })
      }, 800)

      // Call marketplace landing endpoint
      const result = await apiClient.get<MarketplaceLandingResponse>(
        `/marketplace/landing?token=${encodeURIComponent(token)}`
      )

      clearInterval(stepInterval)
      setActiveStep(ACTIVATION_STEPS.length)

      if (result.success) {
        setSuccess(true)
        setResponse(result)

        // Redirect after short delay
        setTimeout(() => {
          if (result.redirectUrl) {
            navigate(result.redirectUrl)
          } else {
            // Default to setup page
            navigate(`/marketplace/setup?tenantId=${result.tenantId}`)
          }
        }, 2000)
      } else {
        setError(result.message || 'Failed to activate subscription')
      }
    } catch (err: any) {
      setError(
        err.message ||
        'An error occurred while activating your subscription. Please contact support if this persists.'
      )
      console.error('Marketplace activation error:', err)
    } finally {
      setLoading(false)
    }
  }

  if (!token) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`
        }}
      >
        <Container maxWidth="sm">
          <Card elevation={8}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom fontWeight="bold">
                Invalid Activation Link
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                The marketplace activation link appears to be invalid or incomplete.
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Please check your email from Microsoft for the correct activation link,
                or contact our support team for assistance.
              </Typography>
              <Button
                variant="contained"
                href="/"
                sx={{ mt: 2 }}
              >
                Go to Home Page
              </Button>
            </CardContent>
          </Card>
        </Container>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`
      }}
    >
      <Container maxWidth="md">
        <Card elevation={8}>
          <CardContent sx={{ p: 5 }}>
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <CloudDone
                sx={{
                  fontSize: 72,
                  color: loading ? 'primary.main' : success ? 'success.main' : 'error.main',
                  mb: 2
                }}
              />
              <Typography variant="h4" gutterBottom fontWeight="bold">
                {loading
                  ? 'Activating Your Subscription'
                  : success
                  ? 'Subscription Activated!'
                  : 'Activation Failed'}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {loading
                  ? 'Please wait while we set up your account...'
                  : success
                  ? 'Your workspace is ready. Redirecting you to setup...'
                  : 'We encountered an issue activating your subscription'}
              </Typography>
            </Box>

            {/* Progress Stepper */}
            {loading && (
              <Box sx={{ my: 4 }}>
                <Stepper activeStep={activeStep} alternativeLabel>
                  {ACTIVATION_STEPS.map((label) => (
                    <Step key={label}>
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                  <CircularProgress size={48} />
                </Box>
              </Box>
            )}

            {/* Success State */}
            {success && response && (
              <Box sx={{ my: 4 }}>
                <Alert
                  severity="success"
                  icon={<CheckCircle fontSize="large" />}
                  sx={{ mb: 3 }}
                >
                  <Typography variant="body1" fontWeight="medium">
                    {response.message}
                  </Typography>
                </Alert>

                <Box
                  sx={{
                    p: 3,
                    bgcolor: alpha(theme.palette.success.main, 0.08),
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
                  }}
                >
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Subscription Details:
                  </Typography>
                  <Box sx={{ display: 'grid', gap: 1, mt: 2 }}>
                    {response.planId && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" fontWeight="medium">
                          Plan:
                        </Typography>
                        <Typography variant="body2">{response.planId}</Typography>
                      </Box>
                    )}
                    {response.subscriptionId && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" fontWeight="medium">
                          Subscription ID:
                        </Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                          {response.subscriptionId.substring(0, 20)}...
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>

                <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">
                    Redirecting you to complete account setup...
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Error State */}
            {error && !loading && (
              <Box sx={{ my: 4 }}>
                <Alert severity="error" icon={<ErrorIcon fontSize="large" />} sx={{ mb: 3 }}>
                  <Typography variant="body1" fontWeight="medium">
                    {error}
                  </Typography>
                </Alert>

                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    If you continue to experience issues, please contact our support team
                    with your subscription reference.
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={() => activateMarketplaceSubscription()}
                    sx={{ mr: 2 }}
                  >
                    Try Again
                  </Button>
                  <Button
                    variant="contained"
                    href="/contact"
                  >
                    Contact Support
                  </Button>
                </Box>
              </Box>
            )}

            {/* Help Text */}
            <Box sx={{ mt: 4, pt: 3, borderTop: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="caption" color="text.secondary" align="center" display="block">
                Purchased through Microsoft Azure Marketplace
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  )
}
